import * as React from 'react';
import { cn, Tooltip } from '@insolech/ui';
import { Info } from 'lucide-react';

/**
 * Shared building blocks for the prescription modification cards
 * (rearfoot / midfoot / forefoot / offloadings / sandal).
 *
 * Compact density per Rafael's UX feedback - text-xs labels, h-7 inputs,
 * tight gaps. Every card has the same L/R two-row layout so podiatrists
 * scan the form fluently.
 */

export function ModCard({
  title,
  subtitle,
  info,
  image,
  id,
  children,
}: {
  title: string;
  subtitle?: string;
  /** Optional tooltip body shown when the user hovers the i icon. */
  info?: React.ReactNode;
  /** Optional reference image rendered top-right (64x64, sand-50 bg). */
  image?: { src: string; alt: string } | undefined;
  /** Stable DOM id so the prescription assistant chips can scroll-to-card. */
  id?: string | undefined;
  children: React.ReactNode;
}) {
  const header = (
    <div className="flex items-start gap-2">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-text-primary leading-tight">{title}</div>
        {subtitle ? (
          <div className="text-[11px] text-text-muted mt-0.5">{subtitle}</div>
        ) : null}
      </div>
      {info ? (
        <Tooltip content={info} side="left">
          <button
            type="button"
            aria-label="More info"
            className="shrink-0 text-text-muted hover:text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-full"
          >
            <Info className="h-4 w-4" aria-hidden="true" />
          </button>
        </Tooltip>
      ) : null}
    </div>
  );
  // scroll-margin-top accounts for the sticky stepper card so jump-to-card
  // doesn't land underneath it.
  const rootClass =
    'h-full rounded-md border border-border-subtle bg-white p-3 space-y-2 scroll-mt-32';
  if (image) {
    return (
      <div id={id} className={rootClass}>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-start">
          <div className="space-y-2 min-w-0">
            {header}
            <div className="space-y-2">{children}</div>
          </div>
          <div className="hidden lg:flex items-center justify-center w-16 h-16 rounded-md bg-sand-50 shrink-0 overflow-hidden self-start">
            <img
              src={image.src}
              alt={image.alt}
              className="h-full w-full object-contain p-1"
              loading="lazy"
              onError={(e) => {
                // Hide the img on 404 so the sand-50 box stays as a
                // clean placeholder instead of showing a broken-image icon
                // (used by cards where the illustration is pending upload).
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div id={id} className={rootClass}>
      {header}
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export function SideRow({
  side,
  children,
}: {
  side: 'L' | 'R';
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-4 text-xs font-mono text-text-muted">{side}</span>
      <div className="flex-1 flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

export function SelectSm<T extends string>({
  value,
  onChange,
  options,
  disabled,
  placeholder,
  className,
  invalid,
  unavailableLabel,
}: {
  value: T | undefined;
  onChange: (v: T | undefined) => void;
  options: ReadonlyArray<{ value: T; label: string }>;
  disabled?: boolean | undefined;
  placeholder?: string | undefined;
  className?: string | undefined;
  /** External force-invalid flag — paints a red border when the wizard
   *  has flagged this select as a required-but-empty dependent of an
   *  upstream choice (e.g. softness/thickness after a non-None location). */
  invalid?: boolean | undefined;
  /** Human label for a value absent from `options`, when the caller knows one.
   *  Without it the raw stored key is shown, which is ugly but unambiguous. */
  unavailableLabel?: string | undefined;
}) {
  // A value the product does not offer.
  //
  // Left alone, a <select> whose value matches no <option> has selectedIndex
  // -1 and renders EMPTY, while the value stays live in state and prints on
  // the lab sheet. The operator sees a blank box and the lab receives a spec.
  // That silence is the bug, so the value is surfaced as a disabled option
  // that says so, and the control paints as invalid.
  //
  // This is a purely local check: `value` and `options` are both already in
  // hand, so it needs no product knowledge and no new props. It therefore
  // catches illegal values from ANY source (a copied prescription, a subtype
  // changed after the fact, legacy migrated data), not just from reuse.
  const unavailable =
    value !== undefined && value !== ('' as T) && !options.some((o) => o.value === value);

  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange((e.target.value || undefined) as T | undefined)}
      disabled={disabled}
      aria-invalid={invalid || unavailable || undefined}
      className={cn(
        'h-7 max-sm:h-9 rounded-md border bg-surface-elevated px-2 text-xs max-sm:text-base',
        'focus:outline-none focus:ring-2 focus:ring-brand-500',
        invalid || unavailable ? 'border-error-500' : 'border-border-default',
        unavailable ? 'text-error-600' : '',
        className,
      )}
    >
      {/* Placeholder option: disabled + hidden so it shows as the closed
          select's label when no value is picked yet, but doesn't appear
          as a selectable item in the open dropdown list. */}
      <option value="" disabled hidden>
        {placeholder ?? '—'}
      </option>
      {/* NOT hidden, unlike the placeholder: the operator must be able to see
          what the field currently holds. Disabled so it cannot be re-picked
          once they move off it. */}
      {unavailable ? (
        <option value={value as string} disabled>
          {`${unavailableLabel ?? String(value)} (not available for this product)`}
        </option>
      ) : null}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function NumInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  disabled,
  placeholder,
  width = 'w-20',
  suffix,
  invalid,
}: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean | undefined;
  placeholder?: string | undefined;
  width?: string;
  /** Optional unit displayed inside the right edge of the input (°, mm, …). */
  suffix?: string | undefined;
  /** External force-invalid flag — used by required-but-empty fields when
   *  the wizard has flagged "show me the missing inputs". Combines with
   *  the internal out-of-range check. */
  invalid?: boolean;
}) {
  // Browser-native min/max on <input type=number> are validation hints
  // only — they don't block typing or paste, and Number('-8') / Number('199')
  // both parse fine. So we enforce bounds ourselves: block '-' / 'e' / '+'
  // when negatives aren't allowed, clamp to [min,max] on blur, and keep an
  // internal string buffer so partial entries (e.g. "-" while typing "-3")
  // don't fight the user mid-keystroke.
  const allowNegative = min == null || min < 0;
  const [draft, setDraft] = React.useState<string>(
    value == null ? '' : String(value),
  );
  // Re-sync the draft when the parent value changes from outside (defaults,
  // pathology presets, etc.) and the user isn't actively editing.
  const [focused, setFocused] = React.useState(false);
  React.useEffect(() => {
    if (!focused) setDraft(value == null ? '' : String(value));
  }, [value, focused]);

  const isOutOfRange =
    value != null &&
    Number.isFinite(value) &&
    ((min != null && value < min) || (max != null && value > max));
  const showInvalid = invalid === true || isOutOfRange;

  const commit = (raw: string): void => {
    const trimmed = raw.trim();
    if (trimmed === '' || trimmed === '-' || trimmed === '+') {
      onChange(undefined);
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n)) {
      onChange(undefined);
      return;
    }
    const lo = min ?? -Infinity;
    const hi = max ?? Infinity;
    const clamped = Math.min(hi, Math.max(lo, n));
    onChange(clamped);
    // Snap the displayed draft to the clamped value so the user sees what
    // landed in state.
    setDraft(String(clamped));
  };

  const input = (
    <input
      type="number"
      value={draft}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      placeholder={placeholder}
      aria-invalid={showInvalid || undefined}
      inputMode={Number.isInteger(step) ? 'numeric' : 'decimal'}
      onKeyDown={(e) => {
        // Hard-block characters that would produce an out-of-bounds value.
        if (e.key === 'e' || e.key === 'E' || e.key === '+') {
          e.preventDefault();
          return;
        }
        if (e.key === '-' && !allowNegative) {
          e.preventDefault();
        }
      }}
      onChange={(e) => {
        // Permissive while typing: hold a local string so the user can
        // backspace or type intermediate values like '-' without flicker.
        // Strip '-' if negatives aren't allowed (paste path).
        let next = e.target.value;
        if (!allowNegative) next = next.replace(/-/g, '');
        setDraft(next);
        // Mirror to parent state only when the value parses to a finite
        // number; clamping happens on blur so we don't steal focus from
        // someone mid-typing (e.g. '1' on the way to '15').
        if (next === '' || next === '-') {
          onChange(undefined);
          return;
        }
        const n = Number(next);
        if (Number.isFinite(n)) onChange(n);
      }}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        setFocused(false);
        commit(e.target.value);
      }}
      className={cn(
        'h-7 max-sm:h-9 rounded-md border bg-surface-elevated px-2 text-xs max-sm:text-base',
        'focus:outline-none focus:ring-2 focus:ring-brand-500',
        showInvalid ? 'border-error-500' : 'border-border-default',
        suffix ? 'pe-7' : '',
        width,
      )}
    />
  );
  if (!suffix) return input;
  return (
    <div className="relative inline-flex items-center">
      {input}
      <span
        className="pointer-events-none absolute end-2 text-[11px] text-text-muted"
        aria-hidden="true"
      >
        {suffix}
      </span>
    </div>
  );
}

export function CheckSm({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean | undefined;
}) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-text-primary select-none max-sm:py-1">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="h-3.5 w-3.5 max-sm:h-4 max-sm:w-4 rounded border-border-default text-brand-600 focus:ring-brand-500 focus:ring-offset-0"
      />
      {label}
    </label>
  );
}

export function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span className="text-[11px] max-sm:text-xs text-text-muted">{children}</span>;
}

/**
 * Section-level observation textarea - rendered at the bottom of each
 * prescription modification section (rearfoot, midfoot, forefoot,
 * offloadings) so the podiatrist can leave free-text notes scoped to that
 * region without diluting the patient-level notes on step 2.
 *
 * Two rows tall by default; expands manually via the corner resize handle.
 */
export function SectionObservation({
  value,
  onChange,
  disabled,
  placeholder,
}: {
  value: string | undefined;
  onChange: (next: string) => void;
  disabled?: boolean | undefined;
  placeholder?: string | undefined;
}) {
  return (
    <div className="space-y-1">
      <FieldLabel>Observations</FieldLabel>
      <textarea
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={2}
        placeholder={placeholder ?? 'Specific notes for this section…'}
        className={cn(
          'w-full rounded-md border border-border-default bg-surface-elevated px-2 py-1.5 text-xs',
          'focus:outline-none focus:ring-2 focus:ring-brand-500',
          'resize-y',
          disabled ? 'opacity-60 cursor-not-allowed' : '',
        )}
      />
    </div>
  );
}
